import React, { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import useApi from '../../api-wrapper/use-api.js';
import ToggleLinks from '../../ui-components/toggle-links/toggle-links.jsx';
import { CATEGORY } from '../../utils/constants.js';
import { scrollToReference } from '../../utils/document-utils.js';
import ChartRecommendationStructural from '../shared/charts-and-grids/chart-recommendation-structural/chart-recommendation-structural.jsx';
import ChartRecommendationTactical from '../shared/charts-and-grids/chart-recommendation-tactical/chart-recommendation-tactical.jsx';
import {
   displayFromToDate,
   generateGetUrlDashboardSummary,
   getFilteredSummaryData,
   getFiltersRawData,
   getFromToDate,
   RECOMMENDATION_STATUS_KEYS,
   RECOMMENDATION_STATUS_TABS,
} from '../shared/charts-and-grids/charts-util.js';
import GridRecommendationStructural from '../shared/charts-and-grids/grid-recommendation-structural/grid-recommendation-structural.jsx';
import GridRecommendationTactical from '../shared/charts-and-grids/grid-recommendation-tactical/grid-recommendation-tactical.jsx';

const RecommendationContainer = ({
   searchCriteriaPayload,
   recommCompetitor,
   setIsOpenSearchModalHandler,
   setIsShowNotification,
   filterBy,
   onChangeFilterName,
   setCommonFiltersHandler,
   initialCategoryType,
   posOnClickHandler,
   reasonsFilter,
   resetReasonFiltersHandler,
}) => {
   const [structuralSummaryData, fetchStructuralSummaryData] = useApi([
      { method: 'GET', url: searchCriteriaPayload },
   ]);
   const [filteredStructuralSummaryData, setFilteredStructuralSummaryData] =
      useState([]);
   const [tacticalSummaryData, fetchTacticalSummaryData] = useApi([
      { method: 'GET', url: searchCriteriaPayload },
   ]);
   const [filteredTacticalSummaryData, setFilteredTacticalSummaryData] =
      useState([]);
   const [recommendationStatus, setRecommendationStatus] = useState(
      RECOMMENDATION_STATUS_KEYS.RECOMMENDATION
   );
   const [categoryType, setCategoryType] = useState(initialCategoryType);
   const gridRef = useRef();

   const getSummaryData = (chartData) =>
      chartData ? chartData.data.recommendationSummaryList : [];
   const executeScroll = () => scrollToReference(gridRef);

   useEffect(() => {
      const summaryData = getSummaryData(structuralSummaryData);
      if (isEmpty(summaryData)) {
         setIsOpenSearchModalHandler(true);
         if (
            structuralSummaryData &&
            structuralSummaryData.data &&
            structuralSummaryData.data.totalCount === 0
         ) {
            setIsShowNotification(true);
         }
      } else {
         const fromTo = getFromToDate(searchCriteriaPayload);
         const availableFilters = getFiltersRawData(summaryData, fromTo);
         setCommonFiltersHandler(
            availableFilters,
            `${CATEGORY.STRUCTURAL}_SUMMARY`
         );
         setIsOpenSearchModalHandler(false);
         setFilteredStructuralSummaryData(
            getFilteredSummaryData(
               summaryData,
               filterBy,
               recommendationStatus,
               recommCompetitor
            )
         );
      }
   }, [structuralSummaryData]);

   useEffect(() => {
      const summaryData = getSummaryData(tacticalSummaryData);
      if (isEmpty(summaryData)) {
         setIsOpenSearchModalHandler(true);
         if (
            tacticalSummaryData &&
            tacticalSummaryData.data &&
            tacticalSummaryData.data.totalCount === 0
         ) {
            setIsShowNotification(true);
         }
      } else {
         const fromTo = getFromToDate(searchCriteriaPayload);
         const availableFilters = getFiltersRawData(summaryData, fromTo);
         setCommonFiltersHandler(
            availableFilters,
            `${CATEGORY.TACTICAL}_SUMMARY`
         );
         setIsOpenSearchModalHandler(false);
         setFilteredTacticalSummaryData(
            getFilteredSummaryData(
               summaryData,
               filterBy,
               recommendationStatus,
               recommCompetitor
            )
         );
      }
   }, [tacticalSummaryData]);

   useEffect(() => {
      fetchStructuralSummaryData(
         generateGetUrlDashboardSummary(
            CATEGORY.STRUCTURAL,
            searchCriteriaPayload,
            recommCompetitor,
            ''
         )
      );
      fetchTacticalSummaryData(
         generateGetUrlDashboardSummary(
            CATEGORY.TACTICAL,
            searchCriteriaPayload,
            recommCompetitor,
            ''
         )
      );
   }, [searchCriteriaPayload]);

   useEffect(() => {
      if (!isEmpty(structuralSummaryData)) {
         setFilteredStructuralSummaryData(
            getFilteredSummaryData(
               getSummaryData(structuralSummaryData),
               filterBy,
               recommendationStatus,
               recommCompetitor,
               onChangeFilterName
            )
         );
      }
      if (!isEmpty(tacticalSummaryData)) {
         setFilteredTacticalSummaryData(
            getFilteredSummaryData(
               getSummaryData(tacticalSummaryData),
               filterBy,
               recommendationStatus,
               recommCompetitor,
               onChangeFilterName
            )
         );
      }
   }, [filterBy, onChangeFilterName]);

   const updateFilteredStructuralSummaryData = (recommendationStatus) => {
      setFilteredStructuralSummaryData(
         getFilteredSummaryData(
            getSummaryData(structuralSummaryData),
            filterBy,
            recommendationStatus,
            recommCompetitor
         )
      );
   };

   const updateFilteredTacticalSummaryData = (recommendationStatus) => {
      setFilteredTacticalSummaryData(
         getFilteredSummaryData(
            getSummaryData(tacticalSummaryData),
            filterBy,
            recommendationStatus,
            recommCompetitor
         )
      );
   };

   const handleStructuralPosOnClick = (pos, reasonsDropdownData) =>
      handlePosOnClick(CATEGORY.STRUCTURAL, pos, reasonsDropdownData);
   const handleTacticalPosOnClick = (pos, reasonsDropdownData) =>
      handlePosOnClick(CATEGORY.TACTICAL, pos, reasonsDropdownData);
   const handlePosOnClick = (category, pos, reasonsDropdownData) => {
      posOnClickHandler(category, pos, reasonsDropdownData);
      setRecommendationStatus(RECOMMENDATION_STATUS_KEYS.NO_RECOMMENDATION);
      setCategoryType('');
      setTimeout(() => {
         setCategoryType(category);
         executeScroll();
      }, 500);
   };

   const dataGridContainer =
      categoryType === CATEGORY.STRUCTURAL &&
      !isEmpty(filteredStructuralSummaryData) ? (
         <GridRecommendationStructural
            key={displayFromToDate(filterBy)}
            searchCriteriaPayload={searchCriteriaPayload}
            recommendationStatus={recommendationStatus}
            recommendationStructuralSummaryData={filteredStructuralSummaryData}
            filterBy={filterBy}
            posOnClickHandler={handleStructuralPosOnClick}
            reasonsFilter={reasonsFilter}
         />
      ) : categoryType === CATEGORY.TACTICAL &&
        !isEmpty(filteredTacticalSummaryData) ? (
         <GridRecommendationTactical
            key={displayFromToDate(filterBy)}
            searchCriteriaPayload={searchCriteriaPayload}
            recommendationStatus={recommendationStatus}
            recommendationTacticalSummaryData={filteredTacticalSummaryData}
            filterBy={filterBy}
            posOnClickHandler={handleTacticalPosOnClick}
            reasonsFilter={reasonsFilter}
         />
      ) : null;

   const handleRecommendationStatusTabsOnClick = (recommStatus) => {
      setRecommendationStatus(recommStatus);
      setCategoryType('');
      resetReasonFiltersHandler({});
   };

   const handleGirdChartIconOnClick = (categoryTypeNew) => {
      setCategoryType(categoryTypeNew);
      executeScroll();
   };

   return (
      <>
         <ToggleLinks
            toggleLinkOnClickHandler={handleRecommendationStatusTabsOnClick}
            selectedToggleLink={recommendationStatus}
            toggleLinks={RECOMMENDATION_STATUS_TABS}
         />
         <div className='row fo-spark-mar-t-4'>
            <div className='col-xs-12 col-sm-12 col-md-6'>
               <ChartRecommendationStructural
                  filteredStructuralSummaryData={filteredStructuralSummaryData}
                  searchCriteriaPayload={searchCriteriaPayload}
                  recommendationStatus={recommendationStatus}
                  updateFilteredStructuralSummaryData={
                     updateFilteredStructuralSummaryData
                  }
                  gridChartIconOnClickHandler={handleGirdChartIconOnClick}
                  filterBy={filterBy}
               />
            </div>
            <div className='col-xs-12 col-sm-12 col-md-6 spark-mar-t-2--lte-sm'>
               <ChartRecommendationTactical
                  filteredTacticalSummaryData={filteredTacticalSummaryData}
                  searchCriteriaPayload={searchCriteriaPayload}
                  recommendationStatus={recommendationStatus}
                  updateFilteredTacticalSummaryData={
                     updateFilteredTacticalSummaryData
                  }
                  gridChartIconOnClickHandler={handleGirdChartIconOnClick}
                  filterBy={filterBy}
               />
            </div>
         </div>
         <div className='row fo-spark-mar-t-4' ref={gridRef}>
            {dataGridContainer}
         </div>
      </>
   );
};

RecommendationContainer.propTypes = {};

export default RecommendationContainer;
